import { useLayoutEffect } from 'react';

/**
 * Título da aba, meta description e `noindex` da página que está na tela.
 *
 * O site é um SPA com um `index.html` só: sem isto, toda URL saía com o mesmo título e a mesma
 * descrição — a da Home —, e o Google recebia oito páginas públicas que se apresentavam como uma.
 *
 * Os valores de `public/index.html` são lidos UMA vez, na carga do módulo, e são o que a página
 * devolve ao desmontar. Assim quem não chama o hook herda o padrão, e não o título da tela anterior.
 *
 * `useLayoutEffect`, e não `useEffect`: o componente Analytics (src/App.jsx) envia o `page_view`
 * com `document.title` num efeito comum, e todos os layout effects de um commit rodam antes dos
 * efeitos comuns. Com `useEffect` o relatório receberia o título da tela anterior. A exceção é a
 * primeira abertura de uma rota `lazy`: o Analytics dispara durante o "Carregando…", e aí vai o
 * título padrão — genérico, mas nunca o de outra página.
 *
 * `indexar: false` põe `<meta name="robots" content="noindex">`. Só funciona se a URL NÃO estiver
 * bloqueada no robots.txt: o Google precisa rastrear a página para ler a tag.
 *
 * `canonical` (o caminho, "/diaristas/curitiba-pr") põe `<link rel="canonical">` com a URL absoluta
 * do site em produção. Hoje só as páginas por cidade e a /diaristas o passam: o index.html é o mesmo
 * em todas as URLs, e sem a tag o Google decide sozinho qual das parecidas representa o grupo.
 */

// A mesma origem do ORIGEM de scripts/gerar-sitemap.js. Cravada, e não `location.origin`: o
// canonical aponta para a URL do sitemap mesmo quando a página é aberta sem o www ou no localhost.
const ORIGEM_CANONICA = 'https://www.novafaxina.com.br';

const TITULO_PADRAO = typeof document !== 'undefined' ? document.title : '';

const metaDescricao = () =>
    typeof document !== 'undefined' ? document.querySelector('meta[name="description"]') : null;

const DESCRICAO_PADRAO = metaDescricao()?.getAttribute('content') ?? '';

export default function useMetadadosPagina({ titulo, descricao, indexar = true, canonical } = {}) {
    useLayoutEffect(() => {
        if (titulo) document.title = titulo;

        const meta = metaDescricao();
        if (descricao && meta) meta.setAttribute('content', descricao);

        let robots = null;
        if (!indexar) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            robots.setAttribute('content', 'noindex');
            document.head.appendChild(robots);
        }

        let linkCanonical = null;
        if (canonical) {
            linkCanonical = document.createElement('link');
            linkCanonical.setAttribute('rel', 'canonical');
            linkCanonical.setAttribute('href', `${ORIGEM_CANONICA}${canonical}`);
            document.head.appendChild(linkCanonical);
        }

        return () => {
            document.title = TITULO_PADRAO;
            if (meta) meta.setAttribute('content', DESCRICAO_PADRAO);
            robots?.remove();
            linkCanonical?.remove();
        };
    }, [titulo, descricao, indexar, canonical]);
}
